import axios from 'axios';
import api from './api';
import { getApiUrl } from './cognitoPool';

export interface LegalVersionRecord {
  documentType: string;
  version: string;
  s3Key: string;
  effectiveDate: string;
  uploadedAt: string;
  uploadedBy: string;
  fileName: string;
  fileSize: number;
  presignedUrl: string;
}

export interface CurrentVersionsResponse {
  tos: LegalVersionRecord | null;
  privacy: LegalVersionRecord | null;
}

let publicApiInstance: ReturnType<typeof axios.create> | null = null;

const getPublicApiInstance = () => {
  if (!publicApiInstance) {
    const apiUrl = getApiUrl();
    if (!apiUrl) throw new Error('API URL not configured');
    publicApiInstance = axios.create({ baseURL: apiUrl });
  }
  return publicApiInstance;
};

export const _resetPublicApiForTesting = (): void => {
  publicApiInstance = null;
};

export async function getCurrentVersions(): Promise<CurrentVersionsResponse> {
  const response = await getPublicApiInstance().get('/api/legal/current');
  return response.data as CurrentVersionsResponse;
}

export async function uploadDocument(
  documentType: 'tos' | 'privacy',
  file: File
): Promise<LegalVersionRecord> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(
    `/api/legal/documents/${documentType}/versions`,
    formData as unknown as Record<string, unknown>,
    { headers: { 'Content-Type': 'multipart/form-data' } } as Record<string, unknown>
  );
  return response.data as LegalVersionRecord;
}

export async function listVersions(
  documentType: 'tos' | 'privacy'
): Promise<LegalVersionRecord[]> {
  const response = await api.get(`/api/legal/documents/${documentType}/versions`);
  return response.data as LegalVersionRecord[];
}

export interface LegalStatusResponse {
  needsAcceptance: string[];
  currentVersions: Record<string, string>;
}

export interface LegalAcceptRequest {
  document_type: string;
  version: string;
}

export interface LegalAcceptanceRecord {
  documentType: string;
  version: string;
  acceptedAt: string;
}

export async function getLegalStatus(): Promise<LegalStatusResponse> {
  const response = await api.get('/api/legal/status');
  return response.data as LegalStatusResponse;
}

export async function acceptDocument(body: LegalAcceptRequest): Promise<void> {
  await api.post('/api/legal/accept', body);
}

export async function getAcceptanceHistory(): Promise<LegalAcceptanceRecord[]> {
  const response = await api.get('/api/legal/acceptance/history');
  return response.data as LegalAcceptanceRecord[];
}

export function formatVersion(version: string): string {
  return `v${parseInt(version, 10)}`;
}

export function formatFileSize(bytes: number): string {
  if (bytes >= 1_048_576) {
    return `${(bytes / 1_048_576).toFixed(1)} MB`;
  }
  return `${Math.round(bytes / 1024)} KB`;
}
